import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { listDemoProgress } from "../lib/api";
import { PhoneticHeatmap } from "../components/PhoneticHeatmap";
import { NotificationPreview } from "../components/NotificationPreview";
import { fmtDuration } from "../lib/format";
import { useApp } from "../context/AppContext";

export default function PostCallPage() {
  const { id } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { state } = useApp();

  const loadSession = useCallback(async () => {
    if (state.lastSession?.id === id) {
      setSession(state.lastSession);
      setLoading(false);
      return;
    }
    try {
      const list = await listDemoProgress();
      const found = list.find((sessionItem) => sessionItem.id === id);
      setSession(found);
    } finally {
      setLoading(false);
    }
  }, [id, state.lastSession]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  if (loading) return <div className="max-w-[1080px] mx-auto px-6 py-16"><div className="wk-skel" style={{ height: 80 }} /></div>;

  if (!session) {
    return (
      <div className="max-w-[720px] mx-auto px-6 py-24 text-center">
        <h1 className="text-[26px] font-bold tracking-hero" style={{ color: "#1E1924" }}>Esa clase no aparece.</h1>
        <button onClick={() => navigate("/dashboard")} className="wk-btn wk-btn-primary mt-6" data-testid="postcall-dashboard-btn">Ir a mi progreso</button>
      </div>
    );
  }

  return (
    <div>
      {/* Confirmación grande */}
      <div style={{ background: "#FAF9FB", borderBottom: "1px solid #DEDAE2" }}>
        <div className="max-w-[1080px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: "rgba(114,40,200,0.08)", color: "#7228C8" }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#7228C8" }} />
            <span className="text-[12px] font-medium uppercase tracking-wide">Informe listo</span>
          </div>
          <h1 className="mt-6 text-[42px] lg:text-[64px] font-extrabold tracking-hero leading-[1.03]" style={{ color: "#1E1924" }}>
            Colgaste. La IA<br />ya lo ha visto todo.
          </h1>
          <p className="mt-6 text-[16px] leading-[1.6] max-w-prose-tight" style={{ color: "#70667A" }}>
            {fmtDuration(session.duration_seconds)} de conversación con <strong style={{ color: "#1E1924" }}>{session.tutor_display_name}</strong>. Aquí va tu diagnóstico de pronunciación.
          </p>

          {/* KPIs */}
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 lg:gap-10">
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "#70667A" }}>Duración</div>
              <div className="font-mono-num text-[26px] sm:text-[32px] lg:text-[40px] font-extrabold tracking-hero leading-none mt-1.5" style={{ color: "#1E1924" }}>
                {fmtDuration(session.duration_seconds)}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "#70667A" }}>Pack</div>
              <div className="font-mono-num text-[26px] sm:text-[32px] lg:text-[40px] font-extrabold tracking-hero leading-none mt-1.5" style={{ color: "#7228C8" }}>
                $22
              </div>
              <div className="text-[11px] mt-1" style={{ color: "#70667A" }}>2 sesiones de 30 min</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "#70667A" }}>Fluidez</div>
              <div className="font-mono-num text-[26px] sm:text-[32px] lg:text-[40px] font-extrabold tracking-hero leading-none mt-1.5" style={{ color: "#1E1924" }}>
                {session.fluency_score}
              </div>
              <div className="text-[11px] mt-1" style={{ color: "#70667A" }}>sobre 100</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "#70667A" }}>Pronunciación</div>
              <div className="font-mono-num text-[26px] sm:text-[32px] lg:text-[40px] font-extrabold tracking-hero leading-none mt-1.5" style={{ color: "#1E1924" }}>
                {session.pronunciation_score}
              </div>
              <div className="text-[11px] mt-1" style={{ color: "#70667A" }}>sobre 100</div>
            </div>
          </div>
        </div>
      </div>

      {/* Heatmap */}
      <div className="max-w-[1080px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="wk-eyebrow mb-4">Mapa de calor fonético</div>
        <h2 className="text-[32px] lg:text-[44px] font-bold tracking-hero leading-[1.08] max-w-[720px]" style={{ color: "#1E1924" }}>
          Dónde has clavado y dónde te delatas.
        </h2>
        <p className="mt-4 text-[15px] leading-[1.6] max-w-prose-tight" style={{ color: "#70667A" }}>
          Puntuación por fonema, comparando tu audio con el nativo de referencia. Pincha cualquiera para ver el detalle y los ejercicios recomendados.
        </p>
        <div className="mt-10">
          <PhoneticHeatmap />
        </div>
      </div>

      {/* Siguiente paso */}
      <div style={{ background: "#1E1924", color: "#FAF9FB" }}>
        <div className="max-w-[1080px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="wk-eyebrow" style={{ color: "rgba(250,249,251,0.55)" }}>Siguiente paso</div>
          <h2 className="mt-4 text-[32px] lg:text-[44px] font-bold tracking-hero leading-[1.08]">
            Vuelve a hablar y compáralo.
          </h2>
          <div className="mt-10 p-8 rounded-2xl" style={{ background: "rgba(114,40,200,0.15)", border: "1px solid rgba(114,40,200,0.35)" }}>
            <div className="text-[12px] uppercase tracking-[0.14em]" style={{ color: "rgba(250,249,251,0.6)" }}>Tu informe</div>
            <div className="font-mono-num text-[48px] font-extrabold tracking-hero mt-2" style={{ color: "#FAF9FB" }}>Sesión 1</div>
            <div className="text-[13px] mt-3" style={{ color: "rgba(250,249,251,0.65)" }}>
              Guarda este resultado y compáralo tras la segunda conversación del pack.
            </div>
          </div>
          <p className="mt-8 text-[13px]" style={{ color: "rgba(250,249,251,0.55)" }}>
            Tu segundo informe mostrará la evolución fonema a fonema.
          </p>
        </div>
      </div>

      {/* Notification preview */}
      <div className="max-w-[1080px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="wk-eyebrow mb-4">Notificación automática</div>
        <h2 className="text-[28px] lg:text-[36px] font-bold tracking-hero leading-[1.1] max-w-[680px]" style={{ color: "#1E1924" }}>
          Esto es lo que recibirías en tu correo.
        </h2>
        <p className="mt-3 text-[14px]" style={{ color: "#70667A" }}>Simulado. En producción se dispara solo tras cada clase.</p>
        <div className="mt-8">
          <NotificationPreview
            to={`${state.user?.email || "tu@correo.com"} · ${state.user?.name || "Alumno"}`}
            subject={`Tu diagnóstico fonético con ${session.tutor_display_name} está listo`}
            body={`Hola ${state.user?.name?.split(" ")[0] || "hey"},

Acabas de tener ${fmtDuration(session.duration_seconds)} de clase con ${session.tutor_display_name} (${session.tutor_country}, ${session.tutor_accent}). Aquí va el resumen sin adornos:

· Fluidez: ${session.fluency_score}/100 — ${session.fluency_score >= 80 ? "vas bien, sigue así." : "hay margen, ¿lo trabajamos?"}
· Pronunciación: ${session.pronunciation_score}/100.
· Fonema más sólido: ${session.strongest_phoneme}. Fonema a trabajar: ${session.weakest_phoneme}.

Te hemos preparado 3 ejercicios cortos para el ${session.weakest_phoneme}. Los tienes en tu panel esperándote.

Un abrazo,
El equipo de WeSpik`}
          />
        </div>
      </div>

      {/* Next actions */}
      <div style={{ background: "#FAF9FB", borderTop: "1px solid #DEDAE2" }}>
        <div className="max-w-[1080px] mx-auto px-6 lg:px-10 py-16 lg:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h3 className="text-[24px] font-bold tracking-hero" style={{ color: "#1E1924" }}>¿Otra vuelta?</h3>
            <p className="text-[14px] mt-1" style={{ color: "#70667A" }}>Tu siguiente conversación está incluida en el pack.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/tutores" className="wk-btn wk-btn-primary" data-testid="postcall-again-btn">Elegir otro tutor</Link>
            <Link to="/dashboard" className="wk-btn wk-btn-ghost" data-testid="postcall-progress-btn">Ver mi progreso</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
